import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { RefreshCcw, Check, X, Plus, UploadCloud, Loader2 } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TableTools, useTableSearch } from "@/components/TableTools";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export function ChargeBackPanel({ isSuper }) {
  return isSuper ? <SuperChargeBack /> : <BranchChargeBack />;
}

function StatusBadge({ s }) {
  const cls = s === "Approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : s === "Rejected" ? "bg-rose-50 text-rose-700 border-rose-200"
    : "bg-amber-50 text-amber-700 border-amber-200";
  return <span className={`text-xs px-2 py-0.5 rounded-full border ${cls}`}>{s}</span>;
}

function BranchChargeBack() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ tx_id: "", amount: "", note: "", screenshot: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => { api.get("/chargebacks").then((r) => setRows(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const onImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/branch/upload-image", fd);
      setForm((p) => ({ ...p, screenshot: data.url }));
      toast.success("Screenshot uploaded");
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const submit = async () => {
    const amt = parseFloat(form.amount);
    if (!form.tx_id.trim()) return toast.error("Transaction ID is required");
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post("/chargebacks", { tx_id: form.tx_id.trim(), amount: amt, note: form.note || null, screenshot: form.screenshot || null });
      toast.success("Charge back submitted");
      setForm({ tx_id: "", amount: "", note: "", screenshot: "" });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="chargeback-panel-branch">
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
        <div className="flex items-center gap-2"><RefreshCcw className="w-5 h-5 text-teal-600" /><h2 className="text-lg font-bold text-slate-900">Raise a Charge Back</h2></div>
        <p className="text-sm text-slate-500 mt-1">Submit a charge back against a transaction. On Super Admin approval, the amount is credited back to your available limit.</p>
        <div className="mt-4 grid md:grid-cols-3 gap-3">
          <div><Label className="text-xs text-slate-500">Transaction ID <span className="text-rose-500">*</span></Label><Input data-testid="chargeback-txid-input" value={form.tx_id} onChange={(e) => setForm({ ...form, tx_id: e.target.value })} className="mt-1 bg-white border-slate-300" /></div>
          <div><Label className="text-xs text-slate-500">Amount (₹) <span className="text-rose-500">*</span></Label><Input data-testid="chargeback-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 bg-white border-slate-300" /></div>
          <div><Label className="text-xs text-slate-500">Note</Label><Input data-testid="chargeback-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1 bg-white border-slate-300" /></div>
        </div>
        <div className="mt-3">
          <Label className="text-xs text-slate-500">Screenshot (optional)</Label>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="chargeback-screenshot-input" />
          <button type="button" onClick={() => fileRef.current?.click()} className="mt-1 flex items-center gap-2 border-2 border-dashed border-slate-300 rounded-lg px-4 py-2.5 hover:border-teal-400 transition-colors">
            {form.screenshot ? <img src={fileUrl(form.screenshot)} alt="proof" className="w-10 h-10 object-contain rounded bg-white p-0.5 border border-slate-200" /> : (uploading ? <Loader2 className="w-4 h-4 animate-spin text-teal-600" /> : <UploadCloud className="w-4 h-4 text-teal-600" />)}
            <span className="text-xs text-slate-500">{form.screenshot ? "Change screenshot" : "Upload screenshot"}</span>
          </button>
        </div>
        <Button data-testid="chargeback-submit" onClick={submit} disabled={busy} className="mt-4 bg-teal-600 hover:bg-teal-700 text-white"><Plus className="w-4 h-4 mr-1" />{busy ? "Submitting..." : "Submit Charge Back"}</Button>
      </div>

      <CbHistory rows={rows} testid="chargeback-history-branch" />
    </div>
  );
}

function CbHistory({ rows, showBranch, testid }) {
  const r2 = (s) => [...(showBranch ? [s.branch_name || ""] : []), s.tx_id || "", s.amount ?? "", ...(showBranch ? [] : [s.note || ""]), s.status || "", s.reason || "", s.requested_at || ""];
  const [q, setQ, shown] = useTableSearch(rows, r2);
  const cols = [...(showBranch ? ["Branch"] : []), "Transaction ID", "Amount", ...(showBranch ? [] : ["Note"]), "Status", "Reason", "Requested"];
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 overflow-x-auto" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-bold text-slate-900">Charge back history</h3>
        <TableTools id={testid} query={q} onQuery={setQ} cols={cols} rows={shown.map(r2)} title="ABCD Pay — Charge backs" filename="abcdpay-chargebacks" />
      </div>
      {shown.length === 0 ? <p className="text-sm text-slate-500 mt-2">No charge backs yet.</p> : (
        <table className="w-full mt-3 text-sm"><thead><tr className="border-b border-slate-200 text-left text-slate-500">{cols.map((c, i) => <th key={c} className={i === 0 ? "py-2" : ""}>{c}</th>)}</tr></thead>
        <tbody>{shown.map((s) => (
          <tr key={s.id} className="border-b border-slate-100">
            {showBranch && <td className="py-2">{s.branch_name}</td>}
            <td className={`font-mono text-xs ${showBranch ? "" : "py-2"}`}>{s.tx_id}</td><td>{inr(s.amount)}</td>
            {!showBranch && <td className="text-slate-500">{s.note || "—"}</td>}
            <td><StatusBadge s={s.status} /></td><td className="text-slate-500">{s.reason || "—"}</td><td className="text-slate-500">{(s.requested_at || "").slice(0, 10)}</td>
          </tr>))}</tbody></table>
      )}
    </div>
  );
}

function SuperChargeBack() {
  const [pending, setPending] = useState([]);
  const [rows, setRows] = useState([]);
  const load = () => {
    api.get("/chargebacks", { params: { status: "Requested" } }).then((r) => setPending(r.data)).catch(() => {});
    api.get("/chargebacks").then((r) => setRows(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const decide = async (id, action, reason) => {
    try { await api.post(`/chargebacks/${id}/${action}`, action === "reject" ? { reason } : {}); toast.success(`Charge back ${action}d`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-6" data-testid="chargeback-panel-super">
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 overflow-x-auto">
        <h2 className="text-lg font-bold text-slate-900">Pending charge back requests</h2>
        {pending.length === 0 ? <p className="text-sm text-slate-500 mt-2">No pending requests.</p> : (
          <table className="w-full mt-3 text-sm"><thead><tr className="border-b border-slate-200 text-left text-slate-500"><th className="py-2">Branch</th><th>Transaction ID</th><th>Amount</th><th>Note</th><th>Proof</th><th>Actions</th></tr></thead>
          <tbody>{pending.map((s) => (
            <tr key={s.id} className="border-b border-slate-100" data-testid={`pending-chargeback-${s.id}`}>
              <td className="py-2">{s.branch_name}</td><td className="font-mono text-xs">{s.tx_id}</td><td>{inr(s.amount)}</td><td>{s.note || "—"}</td>
              <td>{s.screenshot ? <a href={fileUrl(s.screenshot)} target="_blank" rel="noreferrer" className="text-teal-700 text-xs underline">View</a> : "—"}</td>
              <td className="flex gap-2 py-2">
                <Button size="sm" data-testid={`approve-chargeback-${s.id}`} onClick={() => decide(s.id, "approve")} className="bg-teal-600 hover:bg-teal-700 text-white h-8"><Check className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" data-testid={`reject-chargeback-${s.id}`} onClick={() => { const r = prompt("Reason for rejection?"); if (r !== null) decide(s.id, "reject", r); }} className="border-rose-200 text-rose-600 hover:bg-rose-50 h-8"><X className="w-3.5 h-3.5" /></Button>
              </td>
            </tr>))}</tbody></table>
        )}
      </div>
      <CbHistory rows={rows} showBranch testid="chargeback-history-super" />
    </div>
  );
}
